/**
 * Error boundary with onReset — wraps the routed app so a crashed page
 * never blanks the whole SPA.
 *
 * It sits outside I18nProvider (app/providers.tsx), so its notice reads the
 * translation layer's instance directly through useTranslation(), which
 * works without the provider. The crash's own message is written for
 * developers, in English: it goes under Details for support, not into the
 * sentence a person reads (docs/help/errors.md, rule 3).
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "@/lib/i18n";

/** What a person sees in place of a screen that crashed while rendering. */
export function CrashNotice({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      style={{
        padding: 40,
        maxInlineSize: 560,
        marginInline: "auto",
        textAlign: "center",
      }}
    >
      <h1 style={{ fontSize: 20 }}>{t("crash.title")}</h1>
      <p className="atlas-muted">{t("crash.body")}</p>
      {error.message && (
        <details className="atlas-muted" style={{ marginBlockEnd: 16 }}>
          <summary>{t("common.details")}</summary>
          <code className="atlas-mono" lang="en" dir="ltr">
            {error.message}
          </code>
        </details>
      )}
      <button
        type="button"
        className="atlas-btn atlas-btn--primary"
        onClick={onRetry}
      >
        {t("common.tryAgain")}
      </button>
    </div>
  );
}

interface Props {
  children: ReactNode;
  resetKeys?: unknown[];
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Forward to Sentry when configured (VITE_SENTRY_DSN).
    if (
      import.meta.env.VITE_SENTRY_DSN &&
      (
        window as {
          Sentry?: { captureException: (e: Error, i: ErrorInfo) => void };
        }
      ).Sentry
    ) {
      (
        window as {
          Sentry?: { captureException: (e: Error, i: ErrorInfo) => void };
        }
      ).Sentry?.captureException(error, info);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.resetKeys !== this.props.resetKeys) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <CrashNotice
        error={this.state.error}
        onRetry={() => this.setState({ error: null })}
      />
    );
  }
}
